import React from "react";

import {
  bindSingleAssetAgentView,
  getSingleAssetBindingView,
  submitBindingLedger,
} from "../../api/client";
import { HoldingEditDialog } from "./HoldingEditDialog";
import { NewSessionDialog } from "./NewSessionDialog";

const openKey = () => `ledger-open-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

function targetFromView(binding, target, facts, fallbackAssetCode) {
  return {
    binding,
    assetCode: binding.asset_code || fallbackAssetCode,
    assetName: target?.asset_name || null,
    // create_on_write 还没有目标，revision 为空，服务端忽略它
    revision: target?.revision ?? null,
    cleared: target?.status === "voided",
    openingPosition: facts?.opening_position ?? null,
    transactions: facts?.transactions ?? [],
  };
}

// 工作台的两个模态框。它们和主流程没有耦合，只吃状态和回调，放在这里让
// SingleAssetAgentDev 回到「会话 + 绑定 + 视图」这一件事上。
export function SingleAssetDialogs({
  activeBinding,
  activeSlotRef,
  assetCode,
  assetType,
  busy,
  conversationId,
  holdingEditOpen,
  newSessionOpen,
  onApplyBindingView,
  holdingEditProposal,
  onHoldingEditCancel,
  onNewSessionCancel,
  onNewSessionConfirm,
  onSetRevision,
  pageView,
  revision,
}) {
  // 弹窗提交的是完整账本：没回传的旧交易会被删掉。所以预填必须来自**本次要写入的
  // 那个目标**，而且 revision 在打开时就钉住（spec §3.3）。原来提交时才绑定、历史却取自
  // 页面上碰巧展示的数据——行情页打开已有持仓的标的时历史是空的，整本账提交就是全删。
  const [target, setTarget] = React.useState(null);
  const openKeyRef = React.useRef(null);

  React.useEffect(() => {
    if (!holdingEditOpen) {
      openKeyRef.current = null;
      setTarget(null);
      return undefined;
    }
    // StrictMode 下 effect 会跑两遍；同一次打开复用同一个幂等键，第二次绑定是服务端重放。
    openKeyRef.current ??= openKey();
    // 右侧正显示的就是写入目标的绑定视图时直接用它（同一个绑定、同一份 facts 和 revision），
    // 不再多等一次带核算的读取；别处改过由提交时的 revision 拦下（用户 2026-09-15：不要「正在读取」过渡）。
    const reusable = pageView?.target && pageView.binding?.binding_id === activeBinding?.binding_id
      && !needsWritableBinding();
    if (reusable) {
      setTarget({ status: "ready", ...targetFromView(activeBinding, pageView.target, {
        transactions: pageView.rows, opening_position: pageView.opening_position,
      }, activeBinding.asset_code) });
      return undefined;
    }
    let cancelled = false;
    setTarget({ status: "loading" });
    resolveWriteTarget(openKeyRef.current)
      .then((resolved) => { if (!cancelled) setTarget({ status: "ready", ...resolved }); })
      .catch((error) => { if (!cancelled) setTarget({ status: "error", error }); });
    return () => { cancelled = true; };
    // 只在打开那一刻取一次快照：提案、绑定都以打开时为准。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdingEditOpen]);

  // 三种情况都要先换一个可写的绑定：
  //   1. 压根还没有绑定（空会话里打一句话就走到这，propose 档不建绑定）；
  //   2. 当前是行情视图（只读）；
  //   3. 当前绑定指着别的标的——直接写就是 AGENT-085。
  // 写进哪只标的，以提案为准——用户是对着那句话解析出来的内容点的提交。
  const wantedAssetCode = holdingEditProposal?.asset_code || activeBinding?.asset_code || assetCode;
  function needsWritableBinding() {
    return !activeBinding?.binding_id
      || activeBinding.view_type === "market"
      || Boolean(wantedAssetCode && activeBinding.asset_code !== wantedAssetCode);
  }

  async function resolveWriteTarget(idempotencyKey) {
    let binding = activeBinding;
    // 这只标的已有持仓（在持或清仓移出）时，服务端会直接绑到那份持仓，而不是新开工作区。
    if (needsWritableBinding()) {
      if (!conversationId || !wantedAssetCode) {
        throw new Error("当前页面还没有可写入的标的，请先打开一只标的再记录交易。");
      }
      binding = await bindSingleAssetAgentView(
        conversationId,
        revision,
        { type: "workspace", asset_code: wantedAssetCode },
        { idempotencyKey },
      );
      // 只记会话 revision（绑定推进了它，下一句对话要带新值）。页面绑定等提交成功再换：
      // 打开就换的话，行情视图被清掉而持仓视图没人加载，取消弹窗后右侧整块空白（T11 实测）。
      onSetRevision(binding.conversation_revision);
    }
    const view = await getSingleAssetBindingView(conversationId, binding.binding_id);
    return targetFromView(binding, view.target, view.facts, wantedAssetCode);
  }

  async function submitHoldingEdit(payload) {
    if (target?.status !== "ready") {
      throw new Error("还没有读到这只标的的记录，请稍候再提交。");
    }
    const view = await submitBindingLedger(conversationId, target.binding.binding_id, {
      assetCode: target.assetCode,
      openingPositions: payload.opening_positions,
      transactions: payload.transactions,
      expectedTargetRevision: target.revision,
    });
    activeSlotRef.current = { conversationId, bindingId: target.binding.binding_id };
    // 端点返回的就是绑定视图，直接接回右侧（页面绑定也随它切到写入目标），省掉一次 GET。
    onApplyBindingView(view);
    // 提交成功后弹窗里继续操作，要以落库后的事实和 revision 为准。
    setTarget({
      ...target,
      revision: view.target?.revision ?? null,
      cleared: view.target?.status === "voided",
      openingPosition: view.facts?.opening_position ?? null,
      transactions: view.facts?.transactions ?? [],
    });
    return {
      warnings: view.warnings ?? [],
      calculationStatus: view.calculation?.calculation_status ?? null,
      calculationFailureReason: view.calculation?.calculation_failure?.reason ?? "",
    };
  }

  return (
    <>
      <NewSessionDialog
        open={newSessionOpen}
        busy={busy}
        onCancel={onNewSessionCancel}
        onConfirm={onNewSessionConfirm}
      />
      <HoldingEditDialog
        open={holdingEditOpen}
        assetCode={holdingEditProposal?.asset_code || assetCode}
        assetType={holdingEditProposal?.facts?.asset_type
          || holdingEditProposal?.additions?.find((row) => row?.asset_type)?.asset_type
          || assetType}
        proposal={holdingEditProposal}
        target={target}
        busy={busy}
        onCancel={onHoldingEditCancel}
        onSubmit={submitHoldingEdit}
      />
    </>
  );
}
